<template>
  <div
    role="radiogroup"
    :aria-label="ariaLabel"
    class="flex max-w-full items-stretch gap-[2px] overflow-x-auto rounded-lg bg-gray-100 px-[3px]"
    :class="size === 'sm' ? 'h-8' : 'h-10'"
    :data-testid="testId"
  >
    <!--
      O botão é a área de toque (altura inteira do grupo: 40px no md); a pílula visual
      é o <span> interno, recuado 3px em cima e embaixo — mesmo visual do trilho com padding.
    -->
    <button
      v-for="(option, index) in options"
      :key="option.value"
      ref="buttons"
      type="button"
      role="radio"
      class="group flex shrink-0 items-stretch py-[3px] outline-none disabled:cursor-not-allowed disabled:opacity-40"
      :class="size === 'sm' ? 'h-full' : 'min-h-[40px]'"
      :aria-checked="option.value === modelValue"
      :tabindex="option.value === focusValue ? 0 : -1"
      :disabled="option.disabled"
      :data-testid="`${testId}-${option.value}`"
      @click="select(option)"
      @keydown="onKeydown($event, index)"
    >
      <span
        class="flex items-center whitespace-nowrap rounded-md px-3 text-sm transition-colors group-focus-visible:ring-2 group-focus-visible:ring-inset group-focus-visible:ring-primary30"
        :class="
          option.value === modelValue
            ? 'bg-white font-semibold text-primary30 shadow-sm'
            : 'text-gray-600'
        "
      >
        {{ option.label }}
      </span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

export type SegmentedOption = { value: string; label: string; disabled?: boolean }

const props = withDefaults(
  defineProps<{
    modelValue: string
    options: SegmentedOption[]
    ariaLabel: string
    size?: 'md' | 'sm'
    testId?: string
  }>(),
  { size: 'md', testId: 'segmented-control' }
)
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
const buttons = ref<HTMLButtonElement[]>([])

/** Roving tabindex: o escolhido (ou o 1º habilitado) recebe o Tab. */
const focusValue = computed(() => {
  const selected = props.options.find((o) => o.value === props.modelValue && !o.disabled)
  return (selected ?? props.options.find((o) => !o.disabled))?.value
})

function select(option: SegmentedOption): void {
  if (option.disabled || option.value === props.modelValue) return
  emit('update:modelValue', option.value)
}

function enabledIndexFrom(start: number, step: 1 | -1): number {
  const total = props.options.length
  for (let i = 1; i <= total; i += 1) {
    const index = (start + step * i + total) % total
    if (!props.options[index].disabled) return index
  }
  return start
}

async function moveTo(index: number): Promise<void> {
  const option = props.options[index]
  if (!option || option.disabled) return
  // Radio: a seta escolhe e move o foco (WAI-ARIA radiogroup).
  emit('update:modelValue', option.value)
  await nextTick()
  buttons.value[index]?.focus()
}

function onKeydown(event: KeyboardEvent, index: number): void {
  const firstEnabled = props.options.findIndex((o) => !o.disabled)
  const lastEnabled =
    props.options.length - 1 - [...props.options].reverse().findIndex((o) => !o.disabled)
  const next: Record<string, number> = {
    ArrowRight: enabledIndexFrom(index, 1),
    ArrowDown: enabledIndexFrom(index, 1),
    ArrowLeft: enabledIndexFrom(index, -1),
    ArrowUp: enabledIndexFrom(index, -1),
    Home: firstEnabled,
    End: lastEnabled
  }
  if (!(event.key in next)) return
  event.preventDefault()
  void moveTo(next[event.key])
}
</script>
